"use client";
import { FiShoppingCart, FiChevronDown } from "react-icons/fi";
import dynamic from "next/dynamic";

const AttractionsDropdown = dynamic(() => import("./AttractionsDropdown"), { ssr: false });
const DestinationsDropdown = dynamic(() => import("./DestinationsDropdown"), { ssr: false });
const ExploreTripsAdoraDropdown = dynamic(() => import("./ExploreTripsAdoraDropdown"), { ssr: false });
import { createPortal } from "react-dom";
import Image from "next/image";
import { useBottomNavigation } from "@/hooks/customer/useHeader";

const BottomNavigationBar = () => {
  const {
    router,
    activeDropdown,
    setActiveDropdown,
    attractionsRef,
    destinationsRef,
    exploreRef,
    totalQuantity,
    wishlistCount,
  } = useBottomNavigation();

  return (
    <>
      {/* Desktop navigation */}
      <div className="hidden lg:block bg-white z-10 relative pt-[85px]">
        <div className="ta-desktop-home-container pt-3">
          {/* Bottom Navigation Bar */}
          <div className="border-b border-black/10">
            {/* Top Row - Secondary Navigation */}
            <div className="flex items-center justify-between h-12 px-6">
              {/* Left Side Dropdowns - open on click */}
              <div className="flex items-center space-x-2 sm:space-x-8 text-[10px] sm:text-sm relative">
                <div ref={attractionsRef} className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveDropdown((prev) =>
                        prev === "attractions" ? null : "attractions",
                      )
                    }
                    className="flex items-center text-black cursor-pointer group transition-colors btn-text border-0 bg-transparent p-0 cursor-pointer"
                  >
                    <span className="btn-text font-normal group-hover:text-black">
                      Global Spots
                    </span>
                    <FiChevronDown
                      aria-hidden
                      className={`ml-1 w-6 h-6 shrink-0 origin-center text-current transition-transform duration-200 ease-out group-hover:text-black ${
                        activeDropdown === "attractions"
                          ? "-rotate-180"
                          : "rotate-0"
                      }`}
                    />
                  </button>
                  {/* Attractions Dropdown */}
                  {activeDropdown === "attractions" &&
                    createPortal(
                      <div
                        className="absolute left-0 top-full z-40"
                        data-dropdown-panel
                      >
                        <AttractionsDropdown
                          isOpen={activeDropdown === "attractions"}
                          triggerRef={attractionsRef}
                          onClose={() => setActiveDropdown(null)}
                        />
                      </div>,
                      document.body,
                    )}
                </div>

                <div ref={destinationsRef} className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveDropdown((prev) =>
                        prev === "destinations" ? null : "destinations",
                      )
                    }
                    className="flex items-center text-black cursor-pointer group transition-colors btn-text border-0 bg-transparent p-0"
                  >
                    <span className="btn-text font-normal group-hover:text-black">
                      Top Destinations
                    </span>
                    <FiChevronDown
                      aria-hidden
                      className={`ml-1 w-6 h-6 shrink-0 origin-center text-current transition-transform duration-200 ease-out group-hover:text-black ${
                        activeDropdown === "destinations"
                          ? "-rotate-180"
                          : "rotate-0"
                      }`}
                    />
                  </button>
                  {/* Destinations Dropdown */}
                  {activeDropdown === "destinations" &&
                    createPortal(
                      <div
                        className="absolute left-0 top-full z-40"
                        data-dropdown-panel
                      >
                        <DestinationsDropdown
                          isOpen={activeDropdown === "destinations"}
                          triggerRef={destinationsRef}
                          onClose={() => setActiveDropdown(null)}
                        />
                      </div>,
                      document.body,
                    )}
                </div>

                <div ref={exploreRef} className="relative">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveDropdown((prev) =>
                        prev === "explore" ? null : "explore",
                      )
                    }
                    className="flex items-center text-black cursor-pointer group transition-colors btn-text border-0 bg-transparent p-0"
                  >
                    <span className="btn-text font-normal group-hover:text-black">
                      Explore TripsAdora
                    </span>
                    <FiChevronDown
                      aria-hidden
                      className={`ml-1 w-6 h-6 shrink-0 origin-center text-current transition-transform duration-200 ease-out group-hover:text-black ${
                        activeDropdown === "explore"
                          ? "-rotate-180"
                          : "rotate-0"
                      }`}
                    />
                  </button>

                  {/* Explore TripsAdora Dropdown */}
                  {activeDropdown === "explore" &&
                    createPortal(
                      <div
                        className="absolute left-0 top-full z-40"
                        data-dropdown-panel
                      >
                        <ExploreTripsAdoraDropdown
                          isOpen={activeDropdown === "explore"}
                          triggerRef={exploreRef}
                          onClose={() => setActiveDropdown(null)}
                        />
                      </div>,
                      document.body,
                    )}
                </div>
              </div>

              {/* Right Side Icons */}
              <div className="flex items-center space-x-6">
                {/* Compare */}
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => router.push("/comparison")}
                    className="text-black hover:opacity-80 transition cursor-pointer focus:outline-none"
                    aria-label="Compare"
                    // title="Compare"
                  >
                    <Image
                      src="/images/compare.png"
                      alt="Compare"
                      width={22}
                      height={22}
                      className="w-[22px] h-[22px]"
                    />
                  </button>
                  <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-primary px-2 py-1 span-text font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-within:opacity-100">
                    Compare
                  </span>
                </div>

                {/* Favorites */}
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => router.push("/wishlist")}
                    className="text-black hover:opacity-80 transition focus:outline-none relative cursor-pointer"
                    aria-label="Favorites"
                    // title="Favorites"
                  >
                    <svg
                      className="w-[22px] h-[22px]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                    {wishlistCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white span-text font-bold w-4.5 h-4.5 rounded-full flex items-center justify-center">
                        {wishlistCount}
                      </span>
                    )}
                  </button>
                  <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-primary px-2 py-1 span-text font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-within:opacity-100">
                    Favorites
                  </span>
                </div>

                {/* Cart */}
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => router.push("/cart")}
                    className="text-black hover:opacity-80 transition focus:outline-none relative cursor-pointer"
                    aria-label="Cart"
                    // title="Cart"
                  >
                    <FiShoppingCart className="w-[22px] h-[22px]" aria-hidden />

                    {/* Badge: Sirf tab dikhe jab items > 0 hon */}
                    {totalQuantity > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white span-text font-bold w-4.5 h-4.5 rounded-full min-w-[18px] flex items-center justify-center">
                        {totalQuantity}
                      </span>
                    )}
                  </button>

                  <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-primary px-2 py-1 span-text font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-within:opacity-100">
                    Cart
                  </span>
                </div>

                {/* My Bookings */}
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => router.push("/my-bookings")}
                    className="text-black hover:opacity-80 transition focus:outline-none cursor-pointer"
                    aria-label="My bookings"
                    // title="My bookings"
                  >
                    <Image
                      src="/images/CalendarCheck.png"
                      alt="Calendar"
                      width={22}
                      height={22}
                      className="w-[22px] h-[22px]"
                    />
                  </button>
                  <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-primary px-2 py-1 span-text font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-within:opacity-100">
                    My bookings
                  </span>
                </div>
               
              </div>
            </div>
          </div>
        </div>
      </div>
   
    </>
  );
};

export default BottomNavigationBar;
